import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  MessageSquare, 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Star,
  BookOpen,
  Settings,
  User,
  Mail,
  Phone
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supportService, SupportTicket, KnowledgeBaseArticle, SupportCategory } from '../../lib/supportService';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';
import { Badge } from '../common/Badge';
import { SupportTicketForm } from './SupportTicketForm';
import { KnowledgeBase } from './KnowledgeBase';
import { SupportTickets } from './SupportTickets';
import { FeedbackForm } from './FeedbackForm';

type SupportView = 'overview' | 'tickets' | 'knowledge-base' | 'new-ticket' | 'feedback';

export function SupportCenter() {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<SupportView>('overview');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [knowledgeBaseArticles, setKnowledgeBaseArticles] = useState<KnowledgeBaseArticle[]>([]);
  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    if (user) {
      loadSupportData();
    }
  }, [user]);

  const loadSupportData = async () => {
    try {
      setLoading(true);
      const [ticketsData, articlesData, categoriesData] = await Promise.all([
        supportService.getTickets(),
        supportService.getKnowledgeBaseArticles(),
        supportService.getCategories()
      ]);

      setTickets(ticketsData);
      setKnowledgeBaseArticles(articlesData);
      setCategories(categoriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load support data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="text-orange-500" size={16} />;
      case 'in_progress':
        return <Clock className="text-blue-500" size={16} />;
      case 'resolved':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'closed':
        return <XCircle className="text-gray-500" size={16} />;
      default:
        return <AlertCircle className="text-gray-500" size={16} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'urgent':
        return 'bg-orange-100 text-orange-800';
      case 'high':
        return 'bg-yellow-100 text-yellow-800';
      case 'medium':
        return 'bg-blue-100 text-blue-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOpenTickets = () => tickets.filter(ticket => ticket.status === 'open');
  const getInProgressTickets = () => tickets.filter(ticket => ticket.status === 'in_progress');
  const getResolvedTickets = () => tickets.filter(ticket => ticket.status === 'resolved');

  const handleTicketCreated = (newTicket: SupportTicket) => {
    setTickets(prev => [newTicket, ...prev]);
    setShowNewTicketModal(false);
  };

  const handleFeedbackSubmitted = () => {
    setShowFeedbackModal(false);
    // Could show a success message here
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Center</h1>
          <p className="text-gray-600 mt-1">Get help, report issues, and find answers</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => setShowFeedbackModal(true)}
            icon={<Star size={16} />}
          >
            Give Feedback
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowNewTicketModal(true)}
            icon={<Plus size={16} />}
          >
            New Ticket
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setCurrentView('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'overview'
                ? 'border-idecide-primary text-idecide-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setCurrentView('tickets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'tickets'
                ? 'border-idecide-primary text-idecide-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Tickets
          </button>
          <button
            onClick={() => setCurrentView('knowledge-base')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'knowledge-base'
                ? 'border-idecide-primary text-idecide-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Knowledge Base
          </button>
        </nav>
      </div>

      {/* Content */}
      {currentView === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <div className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <AlertCircle className="text-blue-600" size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Open Tickets</p>
                    <p className="text-2xl font-bold text-gray-900">{getOpenTickets().length}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="text-yellow-600" size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">In Progress</p>
                    <p className="text-2xl font-bold text-gray-900">{getInProgressTickets().length}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="text-green-600" size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Resolved</p>
                    <p className="text-2xl font-bold text-gray-900">{getResolvedTickets().length}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BookOpen className="text-purple-600" size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Articles</p>
                    <p className="text-2xl font-bold text-gray-900">{knowledgeBaseArticles.length}</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowNewTicketModal(true)}
                    className="w-full justify-start"
                    icon={<Plus size={16} />}
                  >
                    Create Support Ticket
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentView('knowledge-base')}
                    className="w-full justify-start"
                    icon={<BookOpen size={16} />}
                  >
                    Browse Knowledge Base
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowFeedbackModal(true)}
                    className="w-full justify-start"
                    icon={<Star size={16} />}
                  >
                    Give Feedback
                  </Button>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center text-sm">
                    <Mail className="text-gray-400 mr-3" size={16} />
                    <span>support@idecide.co</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Phone className="text-gray-400 mr-3" size={16} />
                    <span>+61 2 1234 5678</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Clock className="text-gray-400 mr-3" size={16} />
                    <span>Mon-Fri 9AM-5PM AEST</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Recent Tickets */}
          {tickets.length > 0 && (
            <Card>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Recent Tickets</h3>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentView('tickets')}
                    size="sm"
                  >
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {tickets.slice(0, 5).map((ticket) => (
                    <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(ticket.status)}
                        <div>
                          <p className="font-medium text-sm">{ticket.title}</p>
                          <p className="text-xs text-gray-500">#{ticket.ticket_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={getPriorityColor(ticket.priority)}>
                          {ticket.priority}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Featured Knowledge Base Articles */}
          {knowledgeBaseArticles.filter(article => article.is_featured).length > 0 && (
            <Card>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Featured Articles</h3>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentView('knowledge-base')}
                    size="sm"
                  >
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {knowledgeBaseArticles
                    .filter(article => article.is_featured)
                    .slice(0, 3)
                    .map((article) => (
                      <div key={article.id} className="p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-sm mb-1">{article.title}</h4>
                        <p className="text-xs text-gray-500">
                          {article.tags?.slice(0, 3).join(', ')}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {currentView === 'tickets' && (
        <SupportTickets tickets={tickets} onTicketUpdated={loadSupportData} />
      )}

      {currentView === 'knowledge-base' && (
        <KnowledgeBase articles={knowledgeBaseArticles} />
      )}

      {/* Modals */}
      <Modal
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        title="Create Support Ticket"
        size="lg"
      >
        <SupportTicketForm
          categories={categories}
          onTicketCreated={handleTicketCreated}
          onCancel={() => setShowNewTicketModal(false)}
        />
      </Modal>

      <Modal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title="Give Feedback"
        size="md"
      >
        <FeedbackForm
          onFeedbackSubmitted={handleFeedbackSubmitted}
          onCancel={() => setShowFeedbackModal(false)}
        />
      </Modal>
    </div>
  );
} 